import { DebugTransferControls } from "../components/DebugTransferControls";
import { useEffect, useRef, useState } from "react";
import { api, apiFile, type RefundCase, type Taxpayer } from "../api";

type Item = {
  gtip: string; quantity: string; unit_code: string; invoice_no: string;
  invoice_date: string; invoice_series: string; buyer_name: string;
  transaction_code: string; line_amount: string;
};
type Declaration = {
  declaration_type?: "GÇB" | "ETGB" | "GGB"; vkn: string; registration_no: string; registration_date: string; closure_date: string;
  currency: string; invoice_amount: string; import_vat: string; issuing_authority: string; items: Item[];
};
type CustomsDocument = {
  id: number; kind: "ggb" | "gcb"; taxpayer: number; taxpayer_title: string;
  taxpayer_vkn: string; filename: string; confirmed: boolean; declaration: Declaration;
  warnings: string[]; cases: { id: number; title: string; transaction_code: string }[]; source_url: string;
};
type UploadResult = { filename: string; status: string; error?: string; document?: CustomsDocument };
const blankItem = (): Item => ({ gtip: "", quantity: "", unit_code: "", invoice_no: "", invoice_date: "",
  invoice_series: "", buyer_name: "", transaction_code: "", line_amount: "" });

export function CustomsPage({ taxpayers, cases, canPrepare }: {
  taxpayers: Taxpayer[]; cases: RefundCase[]; canPrepare: boolean;
}) {
  const [taxpayer, setTaxpayer] = useState("");
  const [documents, setDocuments] = useState<CustomsDocument[]>([]);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [editor, setEditor] = useState<CustomsDocument | null>(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(`${new Date().getFullYear()}-12-31`);
  const [caseId, setCaseId] = useState("");
  const uploadActive = useRef(false);

  async function load() {
    const rows = await api<CustomsDocument[]>("/api/customs-documents");
    setDocuments(rows);
  }
  useEffect(() => { void load().catch(reason => setError(String(reason))); }, []);
  const visible = documents.filter(doc => !taxpayer || String(doc.taxpayer) === taxpayer);
  const companyCases = cases.filter(c => String(c.taxpayer) === taxpayer);

  async function upload(files: FileList | null, kind: "ggb" | "gcb") {
    if (!files || uploadActive.current) return;
    if (!taxpayer) { setError("Önce şirket seçin."); return; }
    const selected = Array.from(files).filter(file => file.name.toLowerCase().endsWith(".xlsx"));
    if (!selected.length) { setError("3.GGB_TEMPLATE veya 4.GCB_TEMPLATE Excel’i seçin."); return; }
    uploadActive.current = true;
    setBusy(true); setError(""); setMessage(""); setResults([]);
    const collected: UploadResult[] = [];
    try {
      for (let i = 0; i < selected.length; i++) {
        setProgress(`${i + 1}/${selected.length}: ${selected[i].name} okunuyor…`);
        const form = new FormData();
        form.append("kind", kind); form.append("taxpayer", taxpayer); form.append("files", selected[i]);
        try {
          const result = await api<{ results: UploadResult[] }>("/api/customs-documents", { method: "POST", body: form });
          collected.push(...result.results);
        } catch (reason) {
          collected.push({ filename: selected[i].name, status: "failed", error: reason instanceof Error ? reason.message : String(reason) });
        }
        setResults([...collected]);
      }
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { uploadActive.current = false; setBusy(false); setProgress(""); }
  }

  async function download(kind: "gcb" | "sales") {
    setBusy(true); setError("");
    try {
      const params = new URLSearchParams({ taxpayer, start, end });
      const file = await apiFile(`/api/customs-documents/export/${kind}?${params}`);
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement("a"); anchor.href = url;
      anchor.download = file.filename || `${kind}.xls`; anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setBusy(false); }
  }

  async function useInCase() {
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await api<{ detail?: string }>(`/api/refund-cases/${caseId}/use-customs-documents`, { method: "POST" });
      setMessage(result.detail || "Doğrulanmış GÇB / ETGB ve satış kalemleri iade dosyasının kaynak zincirine aktarıldı.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setBusy(false); }
  }

  return <>
    {error && <div className="notice warning" role="alert">{error}</div>}
    {message && <div className="notice">{message}</div>}
    <article className="panel customs-panel"><div className="panel-head"><h2>Şirket</h2></div>
      <div className="customs-filters">
        <label>Şirket<select value={taxpayer} disabled={busy} onChange={event => { setTaxpayer(event.target.value); setCaseId(""); }}>
          <option value="">Şirket seçin</option>{taxpayers.map(t => <option key={t.id} value={t.id}>{t.vkn} · {t.title}</option>)}
        </select></label>
      </div>
    </article>
    <div className="customs-upload-grid">
      {(["ggb", "gcb"] as const).map(kind => <article className="panel" key={kind}>
        <div className="panel-head"><div><p className="eyebrow">{kind === "ggb" ? "İTHALAT" : "İHRACAT"}</p><h2>{kind === "ggb" ? "GGB yükle" : "GÇB / ETGB yükle"}</h2></div></div>
        <p>{kind === "ggb" ? "3.GGB_TEMPLATE.xlsx şablonunu doldurup yükleyin."
          : "4.GCB_TEMPLATE Excel’i, ihracat faturaları ve GTİP kalemleriyle eşleşir. İade dönemi kapanma tarihine göre belirlenir."}</p>
        {canPrepare && <div className="customs-upload-actions">
          <label className="secondary-button">Dosya Seç<input aria-label={`${kind === "ggb" ? "GGB" : "GÇB / ETGB"} Excel dosyaları`} type="file" accept=".xlsx" multiple disabled={busy}
            onChange={event => { void upload(event.target.files, kind); event.target.value = ""; }} /></label>
        </div>}
      </article>)}
    </div>
    {progress && <div className="notice" role="status">{progress}</div>}
    {!!results.length && <article className="panel customs-panel"><h2>Yükleme sonucu</h2>{results.map((result, index) =>
      <div className="customs-result" key={index}><strong>{result.filename}</strong><span>{result.error || (result.status === "duplicate" ? "Zaten yüklü" : "Yüklendi, alan kontrolü bekliyor")}</span></div>)}</article>}
    <article className="panel customs-panel"><div className="panel-head"><h2>Dönem</h2></div>
      <div className="customs-filters">
        <label>Kapanma dönemi başlangıcı<input type="date" value={start} onChange={e => setStart(e.target.value)} /></label>
        <label>Kapanma dönemi bitişi<input type="date" value={end} onChange={e => setEnd(e.target.value)} /></label>
      </div>
      <div className="customs-upload-actions"><button className="secondary-button" disabled={busy || !taxpayer} onClick={() => void download("gcb")}>GÇB / ETGB Listesi indir (.xls)</button>
        <button className="primary-button" disabled={busy || !taxpayer} onClick={() => void download("sales")}>301 ve 338 Satış Faturaları Listesi indir (.xls)</button>
        <DebugTransferControls scope="customs" targetId={taxpayer} disabled={busy} onImported={load} /></div>
      {canPrepare && taxpayer && <div className="customs-case-use"><label>İade dosyası<select value={caseId} onChange={e => setCaseId(e.target.value)}>
        <option value="">Dosya seçin</option>{companyCases.map(c => <option key={c.id} value={c.id}>{c.title} · {c.transaction_code}</option>)}
      </select></label><button className="secondary-button" disabled={busy || !caseId} onClick={() => void useInCase()}>Doğrulanmış belgeleri dosyada kullan</button>
        <small>301/338 hesaplaması için satış faturalarının Faturalar bölümünde bulunması ve eşya döviz tutarlarının tamamlanması gerekir.</small></div>}
    </article>
    <article className="panel"><div className="panel-head"><h2>{visible.length} beyanname</h2></div>
      {!visible.length && <p>Yüklenen beyannameler burada listelenecek.</p>}
      <div className="table-list">{visible.map(doc => <div className="customs-document-row" key={doc.id}>
        <span><strong>{doc.kind === "ggb" ? "GGB" : doc.declaration.declaration_type || "GÇB"} · {doc.declaration.registration_no}</strong><small>{doc.taxpayer_vkn} · {doc.taxpayer_title}</small><small>{doc.filename}</small></span>
        <span><strong>{doc.declaration.invoice_amount || "Tutar eksik"} {doc.declaration.currency}</strong><small>{doc.kind === "ggb" ? `Tescil: ${doc.declaration.registration_date || "Eksik"}` : `Kapanma: ${doc.declaration.closure_date || "Eksik"}`}</small>
          <small>{doc.cases.map(c => `${c.title} (${c.transaction_code})`).join(", ") || "Dönem/kod eşleşen dosya yok"}</small></span>
        <span className={`pill ${doc.confirmed ? "active" : "partial"}`}>{doc.confirmed ? "Doğrulandı" : "Kontrol bekliyor"}</span>
        <button className="text-button" disabled={busy} onClick={() => setEditor(doc)}>{canPrepare ? "Kontrol et / düzenle" : "İncele"}</button>
      </div>)}</div>
    </article>
    {editor && <DeclarationEditor document={editor} canPrepare={canPrepare} onClose={() => setEditor(null)} onSaved={doc => {
      setDocuments(current => current.map(d => d.id === doc.id ? doc : d)); setEditor(null);
    }} />}
  </>;
}

function DeclarationEditor({ document: doc, canPrepare, onClose, onSaved }: {
  document: CustomsDocument; canPrepare: boolean; onClose: () => void; onSaved: (doc: CustomsDocument) => void;
}) {
  const [data, setData] = useState<Declaration>(structuredClone(doc.declaration));
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  function field(key: keyof Omit<Declaration, "items">, label: string, type = "text") {
    return <label>{label}<input type={type} value={data[key] || ""} disabled={!canPrepare || busy || key === "vkn"}
      onChange={e => setData(current => ({ ...current, [key]: e.target.value }))} /></label>;
  }
  function itemField(index: number, key: keyof Item, label: string, type = "text") {
    return <input aria-label={`Kalem ${index + 1} ${label}`} type={type} value={data.items[index][key] || ""} disabled={!canPrepare || busy}
      onChange={e => setData(current => ({ ...current, items: current.items.map((item, i) => i === index ? { ...item, [key]: e.target.value } : item) }))} />;
  }
  async function save(confirmed: boolean) {
    setBusy(true); setError("");
    try { onSaved(await api<CustomsDocument>(`/api/customs-documents/${doc.id}`, {
      method: "PATCH", body: JSON.stringify({ declaration: data, confirmed })
    })); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setBusy(false); }
  }
  return <div className="modal-backdrop" role="presentation"><section className="customs-editor panel" role="dialog" aria-modal="true" aria-label="Beyanname kontrolü">
    <div className="panel-head"><div><h2>{doc.kind === "ggb" ? "GGB" : doc.declaration.declaration_type || "GÇB"} belge kontrolü</h2><small>{doc.filename} · <a href={doc.source_url} target="_blank" rel="noopener noreferrer">Kaynak Excel’i indir</a></small></div><button className="text-button" autoFocus disabled={busy} onClick={onClose}>Kapat</button></div>
    {error && <div className="notice warning" role="alert">{error}</div>}
    <div className="customs-editor-grid"><a className="secondary-button" href={doc.source_url}>Kaynak Excel’i indir</a>
      <div className="customs-fields"><div className="notice warning">{doc.warnings.map((warning, i) => <p key={i}>{warning}</p>)}</div>
        {field("vkn", "Beyanname sahibi VKN")}{field("registration_no", "Tescil numarası")}
        {doc.kind === "gcb" && <label>Beyanname türü<select value={data.declaration_type || "GÇB"} disabled={!canPrepare || busy}
          onChange={e => setData(current => ({ ...current, declaration_type: e.target.value as "GÇB" | "ETGB" }))}>
          <option value="GÇB">GÇB</option><option value="ETGB">ETGB</option>
        </select></label>}
        {field("registration_date", "Tescil tarihi", "date")}
        {field("issuing_authority", "Tescil gümrük müdürlüğü")}
        {doc.kind === "gcb" && field("closure_date", "Kapanma tarihi (gümrük/VEDOP)", "date")}
        {field("currency", "Döviz cinsi")}{field("invoice_amount", "Toplam fatura bedeli (döviz)")}
        {doc.kind === "ggb" && field("import_vat", "Hesaplanan ithalat KDV’si (TL)")}
      </div></div>
    {doc.kind === "gcb" && <><h3>İhraç edilen eşya ve fatura kalemleri</h3><p>Her fatura / GTİP / birim / işlem türü için bir satır. Adet için C62, kilogram için KGM, metrekare için MTK kullanın.</p>
      <div className="customs-items-scroll"><table className="customs-items"><thead><tr>{["GTİP", "Miktar", "Birim", "Fatura tarihi", "Seri", "Fatura no", "Alıcı", "İşlem türü", "Kalem tutarı (döviz)", ""].map((label, i) => <th key={i}>{label}</th>)}</tr></thead>
        <tbody>{data.items.map((item, index) => <tr key={index}>
          <td>{itemField(index, "gtip", "GTİP")}</td><td>{itemField(index, "quantity", "miktar")}</td><td>{itemField(index, "unit_code", "birim")}</td>
          <td>{itemField(index, "invoice_date", "fatura tarihi", "date")}</td><td>{itemField(index, "invoice_series", "seri")}</td>
          <td>{itemField(index, "invoice_no", "fatura no")}</td><td>{itemField(index, "buyer_name", "alıcı")}</td>
          <td><select aria-label={`Kalem ${index + 1} işlem türü`} value={item.transaction_code} disabled={!canPrepare || busy}
            onChange={e => setData(current => ({ ...current, items: current.items.map((row, i) => i === index ? { ...row, transaction_code: e.target.value } : row) }))}>
            <option value="">Seçin</option><option value="301">301</option><option value="338">338</option></select></td>
          <td>{itemField(index, "line_amount", "döviz tutarı")}</td><td>{canPrepare && <button className="text-button danger" disabled={busy} onClick={() => setData(current => ({ ...current, items: current.items.filter((_, i) => i !== index) }))}>Sil</button>}</td>
        </tr>)}</tbody></table></div>
      {canPrepare && <button className="secondary-button" disabled={busy} onClick={() => setData(current => ({ ...current, items: [...current.items, blankItem()] }))}>Kalem ekle</button>}
    </>}
    {canPrepare && <div className="customs-editor-footer"><label><input type="checkbox" checked={checked} disabled={busy} onChange={e => setChecked(e.target.checked)} /> Beyanname alanlarını ve bütün eşya kalemlerini kaynak belgeyle karşılaştırdım.</label>
      <div className="customs-upload-actions"><button className="secondary-button" disabled={busy} onClick={() => void save(false)}>Taslak kaydet</button>
        <button className="primary-button" disabled={busy || !checked} onClick={() => void save(true)}>Doğrula ve kaydet</button></div></div>}
  </section></div>;
}
